<template>
    <div id="selfChangeEmail">
        <FormError :hide-error="!errorCode" :error-code="errorCode"></FormError>

        <accountContainer
            class="narrow narrow-sm"
            :sub-title-text="
                !showNewEmailField
                    ? $t('self-service.change-email.title', { lineBreak: '' })
                    : $t('self-service.change-email.enter-new-email', { lineBreak: '' })
            "
        >
            <template v-if="!showNewEmailField">
                <p>{{ $t('self-service.change-email.desc') }}</p>

                <FormInput
                    id="email"
                    v-model.trim="$v.email.$model"
                    type="email"
                    :has-error="submitted && $v.email.$error"
                    :override-error-text="$t(emailErrorText, [3, 50])"
                    :enter-handler="next"
                    :placeholder="$t('login.email')"
                >
                </FormInput>

                <FormInput
                    id="password"
                    v-model="$v.password.$model"
                    type="password"
                    :max-length="32"
                    :has-error="submitted && $v.password.$error"
                    :override-error-text="$t('error.input.password')"
                    :enter-handler="next"
                    :placeholder="$t('login.password')"
                >
                </FormInput>

                <div class="clearfix"></div>

                <reCaptcha
                    v-if="showCaptcha"
                    v-model="$v.reCaptchaCode.$model"
                    :has-error="$v.reCaptchaCode.$error"
                    :override-error-text="$t('self-service.complete-captcha')"
                    @captcha-completed="setCaptchaCode"
                >
                </reCaptcha>

                <FormButton
                    class="btn-gradient"
                    :loading="buttonLoading"
                    :button-click="next"
                    :label="this.$t('self-service.next')"
                >
                </FormButton>

                <recaptcha-attribution
                    @privacy-click="trackOtherLinkClick('Privacy Policy')"
                    @tos-click="trackOtherLinkClick('Terms of Service')"
                ></recaptcha-attribution>
            </template>

            <template v-else>
                <p>{{ $t('self-service.change-email.enter-new-email-desc') }}</p>

                <FormInput
                    id="newEmail"
                    v-model.trim="$v.newEmail.$model"
                    type="email"
                    :has-error="submitted && $v.newEmail.$error"
                    :override-error-text="$t(emailErrorText, [3, 50])"
                    :enter-handler="changeEmail"
                    :placeholder="$t('self-service.change-email.new-email-address')"
                >
                </FormInput>

                <div class="clearfix"></div>

                <FormButton
                    class="btn-gradient"
                    type="submit"
                    :loading="buttonLoading"
                    :button-click="changeEmail"
                    :label="this.$t('self-service.next')"
                >
                </FormButton>
            </template>

            <ul class="help list-unstyled">
                <li v-if="!showNewEmailField">
                    <button @click="goToVerifyOwnership">
                        <svgIcons icon-holder-class="icon-help" icon-id="icon-page-right" />
                        {{ $t('self-service.no-access-email') }}
                        <span v-if="isVerifyOwnershipLoading" class="spinner-circle resend"></span>
                    </button>
                </li>
                <li>
                    <router-link
                        :to="{ name: 'self-service' }"
                        @click.native="trackOtherLinkClick($t('self-service.back', 'en'))"
                    >
                        <svgIcons icon-holder-class="icon-help" icon-id="icon-page-right" />
                        {{ $t('self-service.back') }}
                    </router-link>
                </li>
            </ul>
        </accountContainer>
    </div>
</template>

<script>
import { FormError, FormButton, FormInput, SvgIcons } from '@arena/toolkit';
import { requiredIf, email, maxLength } from 'vuelidate/lib/validators';
import accountContainer from '@/components/account-container';
import reCaptcha from '@/components/recaptcha';
import recaptchaAttribution from '@/components/recaptcha-attribution';
import { mapGetters } from 'vuex';

export default {
    name: 'SelfServiceChangeEmail',
    components: {
        accountContainer,
        FormInput,
        FormError,
        FormButton,
        reCaptcha,
        recaptchaAttribution,
        SvgIcons,
    },
    metaInfo() {
        return {
            titleTemplate: `%s | ${this.$t('self-service.change-email.title', { lineBreak: '' })}`,
        };
    },
    props: {
        showNewEmailField: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            email: '',
            password: '',
            newEmail: '',
            errorCode: 0,
            buttonLoading: false,
            submitted: false,
            reCaptchaCode: '',
            showCaptcha: false,
            isVerifyOwnershipLoading: false,
        };
    },
    validations() {
        return {
            email: {
                required: requiredIf(() => !this.showNewEmailField),
                email,
                maxLength: maxLength(50),
            },
            password: { required: requiredIf(() => !this.showNewEmailField) },
            reCaptchaCode: {
                required: requiredIf(() => !this.showNewEmailField && this.showCaptcha),
            },
            newEmail: {
                required: requiredIf(() => this.showNewEmailField),
                email,
                maxLength: maxLength(50),
            },
        };
    },
    computed: {
        ...mapGetters({
            serialNumber: 'selfService/getSerialNumber',
        }),
        isApplication() {
            return this.$store.state.isApplication;
        },
        emailErrorText() {
            if (!this.$v.email.required) {
                return 'error.input.email';
            }
            if (!this.$v.newEmail.required) {
                return 'error.input.newEmail';
            }
            if (!this.$v.email.maxLength || !this.$v.newEmail.maxLength) {
                return 'error.input.length.id';
            }
            return 'error.input.not-valid-email';
        },
    },
    mounted() {
        this.$store.commit('selfService/setOperationType', 'changeId');
    },
    methods: {
        setCaptchaCode() {
            this.reCaptchaCode = window.grecaptcha.enterprise.getResponse(window.rcWidgetId);
        },
        goToVerifyOwnership() {
            this.$v.reCaptchaCode.$touch();
            if (!this.isVerifyOwnershipLoading && !this.$v.reCaptchaCode.$error) {
                this.isVerifyOwnershipLoading = true;
                this.$store
                    .dispatch(
                        'selfService/createAuthSession',
                        this.isApplication ? 'launcher_self_change_email' : 'web_self_change_email'
                    )
                    .then(() => {
                        this.isVerifyOwnershipLoading = false;
                        this.trackOtherLinkClick(this.$t('self-service.no-access-email', 'en'));
                        this.$router.push({ name: 'self-verify-ownership' });
                    })
                    .catch(({ errorCode }) => {
                        this.errorHandling(errorCode);
                    });
            }
        },
        next() {
            this.$v.$touch();
            this.submitted = true;
            if (!this.$v.$invalid) {
                this.buttonLoading = true;
                this.errorCode = 0;

                this.$store
                    .dispatch('selfService/getAuthTypes', {
                        searchBy: 'email',
                        value: this.email,
                        password: this.password,
                        captchaAction: this.isApplication ? 'launcher_self_change_email' : 'web_self_change_email',
                    })
                    .catch(({ errorCode }) => {
                        this.errorHandling(errorCode);
                    });
            }
        },
        changeEmail() {
            this.$v.$touch();
            this.submitted = true;
            if (!this.$v.$invalid) {
                this.buttonLoading = true;
                this.errorCode = 0;
                this.$store
                    .dispatch('selfService/newEmailRequestCode', this.newEmail)
                    .then(() => {
                        dataLayer.push({
                            event: 'accountRecoverySelfService_EmailChangeSubmit',
                        });
                        this.$router.push({
                            name: 'self-verify-email',
                            params: {
                                newEmailVerify: this.newEmail,
                            },
                        });
                    })
                    .catch(({ errorCode }) => {
                        this.errorHandling(errorCode);
                    });
            }
        },
        errorHandling(errorCode) {
            this.buttonLoading = false;
            this.isVerifyOwnershipLoading = false;

            if (errorCode === 20072) {
                this.showCaptcha = true;
                dataLayer.push({
                    event: 'recoveryCaptchaTriggered',
                    'AR Recaptcha Source': this.operationType,
                });
                return;
            }
            this.errorCode = errorCode;
        },
        trackOtherLinkClick(name) {
            dataLayer.push({
                event: 'accountRecoverySelfService_OtherLinkClick',
                'AR Link Name': name,
            });
        },
    },
};
</script>

<style lang="less" scoped>
.btn-block {
    margin-top: 5px;
}
.help {
    button {
        padding: 0;
        color: #ccc;
        text-align: left;
        .icon-help {
            width: 8px;
            height: 8px;
            opacity: 0.8;
        }
        .spinner-circle {
            width: 16px;
            height: 16px;
            margin-bottom: -3px;
            margin-left: 5px;
        }
        &:hover {
            color: #fff;
            .icon-help {
                opacity: 1;
            }
        }
    }
}
</style>
